import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import Swal from "sweetalert2";

import {
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    Spin,
    Space,
    Upload,
    Row,
    Col,
    Typography,
    Divider,
    Card,
    Empty,
    Popconfirm,
    Tag,
} from "antd";

import {
    BookOutlined,
    ApartmentOutlined,
    FileImageOutlined,
    VideoCameraOutlined,
    PaperClipOutlined,
    UploadOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FilePptOutlined,
    FileTextOutlined,
    EditOutlined,
    UserOutlined,
    TeamOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    AppstoreOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const RESOURCE_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".csv",
    ".txt",
];

const RESOURCE_ACCEPT =
    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt";

const MAX_RESOURCE_SIZE_MB = 50;

const SubjectFormModal = ({
    open,
    handleClose,
    onSubjectCreated,
    onSubjectUpdated,
    subject = null,
}) => {
    const [form] = Form.useForm();

    const [divisions, setDivisions] = useState([]);

    const [loading, setLoading] = useState(false);

    const [resourceLoading, setResourceLoading] =
        useState(false);

    const [thumbnailFile, setThumbnailFile] =
        useState(null);

    const [videoFile, setVideoFile] =
        useState(null);

    const [existingThumbnail, setExistingThumbnail] =
        useState(null);

    const [existingVideo, setExistingVideo] =
        useState(null);

    const [resources, setResources] =
        useState([]);

    const [resourceFile, setResourceFile] =
        useState(null);

    const [resourceForm, setResourceForm] =
        useState({
            title: "",
            description: "",
        });

    const token =
        localStorage.getItem("access");

    const isEditMode = Boolean(subject?.id);

    const authConfig = useMemo(
        () => ({
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }),
        [token]
    );

    const showCayepAlert = ({
        type = "info",
        title = "",
        text = "",
        timer = null,
        showConfirmButton = true,
    }) => {
        const alertColors = {
            success: {
                accent: "#4FAE22",
                background: "#F4FAEF",
            },
            error: {
                accent: "#C62828",
                background: "#FFF5F5",
            },
            warning: {
                accent: "#EFA81C",
                background: "#FFF9E8",
            },
            info: {
                accent: "#0F6DAB",
                background: "#F2F8FC",
            },
        };

        const colors =
            alertColors[type] ||
            alertColors.info;

        Swal.fire({
            title,
            text,
            icon: type,
            background: colors.background,
            color: "#142536",
            confirmButtonColor: "#062A4B",
            showConfirmButton,
            timer: timer || undefined,
            timerProgressBar: Boolean(timer),
            buttonsStyling: true,
            allowOutsideClick: true,
            customClass: {
                popup: "cayep-swal-popup",
                title: "cayep-swal-title",
                htmlContainer:
                    "cayep-swal-text",
                confirmButton:
                    "cayep-swal-confirm",
            },
            didOpen: (popup) => {
                popup.style.borderTop =
                    `5px solid ${colors.accent}`;

                popup.style.boxShadow =
                    "0 18px 50px rgba(6, 42, 75, 0.18)";
            },
        });
    };

    const getErrorMessage = (
        error,
        fallback
    ) => {
        const errorData =
            error?.response?.data;

        if (
            typeof errorData === "string" &&
            errorData.trim()
        ) {
            return errorData;
        }

        if (errorData?.detail) {
            return String(
                errorData.detail
            );
        }

        if (
            errorData &&
            typeof errorData === "object"
        ) {
            const errors =
                Object.entries(
                    errorData
                )
                    .map(
                        ([field, value]) => {
                            const textValue =
                                Array.isArray(
                                    value
                                )
                                    ? value.join(
                                          ", "
                                      )
                                    : String(
                                          value
                                      );

                            return `${field}: ${textValue}`;
                        }
                    )
                    .filter(Boolean);

            if (errors.length) {
                return errors.join(
                    "\n"
                );
            }
        }

        return fallback;
    };

    const getFileExtension = (
        file
    ) => {
        if (!file) {
            return "";
        }

        const name =
            file.name ||
            file.original_filename ||
            "";

        const lastDot =
            name.lastIndexOf(".");

        if (lastDot === -1) {
            return "";
        }

        return name
            .substring(lastDot + 1)
            .toLowerCase();
    };

    const getResourceExtension = (
        resource
    ) => {
        const filename =
            resource?.original_filename ||
            resource?.file_name ||
            resource?.filename ||
            resource?.name ||
            resource?.title ||
            "";

        const lastDot =
            filename.lastIndexOf(".");

        if (lastDot === -1) {
            return "";
        }

        return filename
            .substring(lastDot)
            .toLowerCase();
    };

    const getResourceIcon = (
        resource
    ) => {
        const extension =
            getResourceExtension(
                resource
            );

        if (extension === ".pdf") {
            return (
                <FilePdfOutlined />
            );
        }

        if (
            extension === ".doc" ||
            extension === ".docx"
        ) {
            return (
                <FileWordOutlined />
            );
        }

        if (
            extension === ".xls" ||
            extension === ".xlsx"
        ) {
            return (
                <FileExcelOutlined />
            );
        }

        if (
            extension === ".ppt" ||
            extension === ".pptx"
        ) {
            return (
                <FilePptOutlined />
            );
        }

        return (
            <FileTextOutlined />
        );
    };

    const getResourceTypeLabel = (
        resource
    ) => {
        const extension =
            getResourceExtension(
                resource
            );

        if (!extension) {
            return "FILE";
        }

        return extension
            .replace(".", "")
            .toUpperCase();
    };

    const getResourceFileName = (
        resource
    ) => {
        return (
            resource?.original_filename ||
            resource?.file_name ||
            resource?.filename ||
            resource?.name ||
            resource?.title ||
            "Learning Resource"
        );
    };

    const getResourceUrl = (
        resource
    ) => {
        return (
            resource?.download_url ||
            resource?.file_url ||
            resource?.file ||
            null
        );
    };

    const isImageFile = (
        file
    ) => {
        if (!file) {
            return false;
        }

        const extension =
            getFileExtension(file);

        const imageExtensions = [
            "jpg",
            "jpeg",
            "png",
            "gif",
            "webp",
            "bmp",
            "avif",
            "heic",
            "heif",
        ];

        const mimeType =
            (
                file.type || ""
            ).toLowerCase();

        return (
            mimeType.startsWith(
                "image/"
            ) ||
            imageExtensions.includes(
                extension
            )
        );
    };

    const isVideoFile = (
        file
    ) => {
        if (!file) {
            return false;
        }

        const extension =
            getFileExtension(file);

        const videoExtensions = [
            "mp4",
            "mov",
            "m4v",
            "webm",
            "avi",
            "mkv",
            "3gp",
            "3gpp",
            "mpeg",
            "mpg",
            "wmv",
            "ogv",
        ];

        const mimeType =
            (
                file.type || ""
            ).toLowerCase();

        return (
            mimeType.startsWith(
                "video/"
            ) ||
            videoExtensions.includes(
                extension
            )
        );
    };

    const isResourceFile = (
        file
    ) => {
        if (!file) {
            return false;
        }

        const extension =
            getFileExtension(file);

        return RESOURCE_EXTENSIONS.includes(
            `.${extension}`
        );
    };

    const fetchDivisions =
        async () => {
            try {
                const response =
                    await api.get(
                        "api/users/divisions/",
                        authConfig
                    );

                const data =
                    response.data;

                setDivisions(
                    Array.isArray(
                        data
                    )
                        ? data
                        : Array.isArray(
                              data?.results
                          )
                        ? data.results
                        : []
                );
            } catch (error) {
                console.error(
                    "Division loading error:",
                    error
                );

                showCayepAlert({
                    type: "error",
                    title:
                        "Departments Could Not Be Loaded",
                    text:
                        "Unable to load the departments. Please refresh the page and try again.",
                });
            }
        };

    const fetchResources =
        async (courseId) => {
            if (!courseId) {
                setResources([]);
                return;
            }

            setResourceLoading(true);

            try {
                const response =
                    await api.get(
                        `api/users/subjects/${courseId}/resources/`,
                        authConfig
                    );

                const data =
                    response.data;

                if (
                    Array.isArray(
                        data
                    )
                ) {
                    setResources(
                        data
                    );
                } else if (
                    Array.isArray(
                        data?.results
                    )
                ) {
                    setResources(
                        data.results
                    );
                } else {
                    setResources([]);
                }
            } catch (error) {
                console.error(
                    "Course resource loading error:",
                    error
                );

                setResources([]);

                showCayepAlert({
                    type: "error",
                    title:
                        "Resources Could Not Be Loaded",
                    text:
                        getErrorMessage(
                            error,
                            "Unable to load the course learning resources."
                        ),
                });
            } finally {
                setResourceLoading(
                    false
                );
            }
        };

    const populateEditForm =
        () => {
            if (!subject) {
                form.resetFields();

                setThumbnailFile(
                    null
                );

                setVideoFile(null);

                setExistingThumbnail(
                    null
                );

                setExistingVideo(
                    null
                );

                setResources([]);

                return;
            }

            form.setFieldsValue({
                name:
                    subject.name ||
                    "",

                facilitator_name:
                    subject.facilitator_name ||
                    "",

                division:
                    subject.division_assigned ??
                    subject.division ??
                    subject.division_id ??
                    undefined,

                description:
                    subject.description ||
                    "",

                category:
                    subject.category ||
                    "",

                duration:
                    subject.duration ||
                    "",

                lessons:
                    subject.lessons ||
                    "",

                level:
                    subject.level ||
                    undefined,

                price:
                    subject.price ??
                    "",

                oldPrice:
                    subject.oldPrice ??
                    subject.old_price ??
                    "",

                slots:
                    subject.slots ??
                    0,
            });

            setThumbnailFile(
                null
            );

            setVideoFile(null);

            setExistingThumbnail(
                subject.image ||
                    subject.image_url ||
                    null
            );

            setExistingVideo(
                subject.video ||
                    subject.video_url ||
                    null
            );

            setResources([]);
        };

    useEffect(() => {
        if (!open) {
            return;
        }

        fetchDivisions();

        populateEditForm();

        setResourceFile(null);

        setResourceForm({
            title: "",
            description: "",
        });

        if (subject?.id) {
            fetchResources(
                subject.id
            );
        } else {
            setResources([]);
        }
    }, [
        open,
        subject,
    ]);

    const beforeThumbnailUpload =
        (file) => {
            if (!isImageFile(file)) {
                showCayepAlert({
                    type: "error",
                    title:
                        "Invalid Thumbnail",
                    text:
                        "Please select a valid image file.",
                });

                return Upload.LIST_IGNORE;
            }

            const sizeMB =
                file.size /
                1024 /
                1024;

            if (sizeMB > 5) {
                showCayepAlert({
                    type: "warning",
                    title:
                        "Image Too Large",
                    text:
                        "The course thumbnail must be smaller than 5 MB.",
                });

                return Upload.LIST_IGNORE;
            }

            setThumbnailFile(
                file
            );

            return false;
        };

    const beforeVideoUpload =
        (file) => {
            if (!isVideoFile(file)) {
                showCayepAlert({
                    type: "error",
                    title:
                        "Invalid Video",
                    text:
                        "Please select a valid video file.",
                });

                return Upload.LIST_IGNORE;
            }

            setVideoFile(file);

            return false;
        };

    const beforeResourceUpload =
        (file) => {
            if (!isResourceFile(file)) {
                showCayepAlert({
                    type: "error",
                    title:
                        "Unsupported Resource",
                    text:
                        "Allowed files are PDF, Word, Excel, PowerPoint, CSV and TXT.",
                });

                return Upload.LIST_IGNORE;
            }

            const fileSizeMB =
                file.size /
                1024 /
                1024;

            if (
                fileSizeMB >
                MAX_RESOURCE_SIZE_MB
            ) {
                showCayepAlert({
                    type: "warning",
                    title:
                        "Resource Too Large",
                    text:
                        `The learning resource must be smaller than ${MAX_RESOURCE_SIZE_MB} MB.`,
                });

                return Upload.LIST_IGNORE;
            }

            setResourceFile(
                file
            );

            setResourceForm(
                (current) => ({
                    ...current,
                    title:
                        current.title ||
                        file.name.replace(
                            /\.[^/.]+$/,
                            ""
                        ),
                })
            );

            return false;
        };

    const uploadResourceToCourse =
        async (
            courseId,
            file,
            title,
            description
        ) => {
            if (
                !courseId ||
                !file
            ) {
                throw new Error(
                    "Missing course ID or resource file."
                );
            }

            const resourceData =
                new FormData();

            resourceData.append(
                "file",
                file,
                file.name
            );

            resourceData.append(
                "title",
                String(
                    title ||
                        file.name.replace(
                            /\.[^/.]+$/,
                            ""
                        )
                ).trim()
            );

            resourceData.append(
                "description",
                String(
                    description || ""
                ).trim()
            );

            await api.post(
                `api/users/subjects/${courseId}/resources/`,
                resourceData,
                authConfig
            );
        };

    const handleDeleteResource =
        async (
            resource
        ) => {
            if (
                !subject?.id ||
                !resource?.id
            ) {
                return;
            }

            setResourceLoading(
                true
            );

            try {
                await api.delete(
                    `api/users/subjects/${subject.id}/resources/${resource.id}/`,
                    authConfig
                );

                setResources(
                    (current) =>
                        current.filter(
                            (item) =>
                                item.id !==
                                resource.id
                        )
                );

                showCayepAlert({
                    type: "success",
                    title:
                        "Resource Deleted",
                    text:
                        "The learning resource has been removed.",
                    timer: 1500,
                    showConfirmButton:
                        false,
                });
            } catch (error) {
                console.error(
                    "Resource deletion error:",
                    error
                );

                showCayepAlert({
                    type: "error",
                    title:
                        "Delete Failed",
                    text:
                        getErrorMessage(
                            error,
                            "Unable to delete this learning resource."
                        ),
                });
            } finally {
                setResourceLoading(
                    false
                );
            }
        };

    const openResource = (
        resource
    ) => {
        const url =
            getResourceUrl(
                resource
            );

        if (!url) {
            showCayepAlert({
                type: "warning",
                title:
                    "File Unavailable",
                text:
                    "The file URL for this resource is not available.",
            });

            return;
        }

        let finalUrl = url;

        if (
            typeof url === "string" &&
            url.startsWith("/")
        ) {
            const baseURL =
                api.defaults
                    ?.baseURL ||
                window.location
                    .origin;

            finalUrl =
                `${baseURL.replace(
                    /\/$/,
                    ""
                )}${url}`;
        }

        window.open(
            finalUrl,
            "_blank",
            "noopener,noreferrer"
        );
    };

    // const handleSubmit =
    //     async (values) => {
    //         if (
    //             !isEditMode &&
    //             !thumbnailFile
    //         ) {
    //             showCayepAlert({
    //                 type: "warning",
    //                 title:
    //                     "Course Thumbnail Required",
    //                 text:
    //                     "Please upload a thumbnail image for the course.",
    //             });

    //             return;
    //         }

    //         if (
    //             !isEditMode &&
    //             !videoFile
    //         ) {
    //             showCayepAlert({
    //                 type: "warning",
    //                 title:
    //                     "Course Video Required",
    //                 text:
    //                     "Please upload the video for this course.",
    //             });

    //             return;
    //         }

    //         if (
    //             !values.facilitator_name ||
    //             !String(
    //                 values.facilitator_name
    //             ).trim()
    //         ) {
    //             showCayepAlert({
    //                 type: "warning",
    //                 title:
    //                     "Facilitator Required",
    //                 text:
    //                     "Please enter the facilitator or instructor name for this course.",
    //             });

    //             form.setFields([
    //                 {
    //                     name:
    //                         "facilitator_name",
    //                     errors: [
    //                         "Facilitator or instructor name is required.",
    //                     ],
    //                 },
    //             ]);

    //             return;
    //         }

    //         const slots =
    //             Number(
    //                 values.slots
    //             );

    //         if (
    //             !Number.isInteger(
    //                 slots
    //             ) ||
    //             slots < 0
    //         ) {
    //             showCayepAlert({
    //                 type: "warning",
    //                 title:
    //                     "Invalid Course Capacity",
    //                 text:
    //                     "Course slots must be a whole number. Use 0 for unlimited enrollment.",
    //             });

    //             form.setFields([
    //                 {
    //                     name:
    //                         "slots",
    //                     errors: [
    //                         "Enter a whole number of slots. Use 0 for unlimited enrollment.",
    //                     ],
    //                 },
    //             ]);

    //             return;
    //         }

    //         setLoading(true);

    //         try {
    //             const formData =
    //                 new FormData();

    //             formData.append(
    //                 "name",
    //                 String(
    //                     values.name ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "facilitator_name",
    //                 String(
    //                     values.facilitator_name ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "division_assigned",
    //                 String(
    //                     values.division
    //                 )
    //             );

    //             formData.append(
    //                 "description",
    //                 String(
    //                     values.description ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "category",
    //                 String(
    //                     values.category ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "duration",
    //                 String(
    //                     values.duration ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "lessons",
    //                 String(
    //                     values.lessons ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "level",
    //                 String(
    //                     values.level ||
    //                         ""
    //                 ).trim()
    //             );

    //             formData.append(
    //                 "slots",
    //                 String(slots)
    //             );

    //             if (
    //                 values.price !==
    //                     undefined &&
    //                 values.price !==
    //                     null &&
    //                 String(
    //                     values.price
    //                 ).trim() !==
    //                     ""
    //             ) {
    //                 formData.append(
    //                     "price",
    //                     String(
    //                         values.price
    //                     ).trim()
    //                 );
    //             }

    //             if (
    //                 values.oldPrice !==
    //                     undefined &&
    //                 values.oldPrice !==
    //                     null &&
    //                 String(
    //                     values.oldPrice
    //                 ).trim() !==
    //                     ""
    //             ) {
    //                 formData.append(
    //                     "old_price",
    //                     String(
    //                         values.oldPrice
    //                     ).trim()
    //                 );
    //             }

    //             if (
    //                 thumbnailFile
    //             ) {
    //                 formData.append(
    //                     "image",
    //                     thumbnailFile,
    //                     thumbnailFile.name
    //                 );
    //             }

    //             if (
    //                 videoFile
    //             ) {
    //                 formData.append(
    //                     "video",
    //                     videoFile,
    //                     videoFile.name
    //                 );
    //             }

    //             const response =
    //                 isEditMode
    //                     ? await api.patch(
    //                           `api/users/subjects/${subject.id}/`,
    //                           formData,
    //                           authConfig
    //                       )
    //                     : await api.post(
    //                           "api/users/subjects/",
    //                           formData,
    //                           authConfig
    //                       );

    //             const courseId =
    //                 response.data?.id ||
    //                 subject?.id;

    //             if (
    //                 !courseId
    //             ) {
    //                 throw new Error(
    //                     "The server did not return the course ID."
    //                 );
    //             }

    //             if (
    //                 resourceFile
    //             ) {
    //                 try {
    //                     await uploadResourceToCourse(
    //                         courseId,
    //                         resourceFile,
    //                         resourceForm.title,
    //                         resourceForm.description
    //                     );
    //                 } catch (
    //                     resourceError
    //                 ) {
    //                     console.error(
    //                         "Course saved but resource upload failed:",
    //                         resourceError
    //                     );

    //                     showCayepAlert({
    //                         type: "warning",
    //                         title:
    //                             isEditMode
    //                                 ? "Course Updated"
    //                                 : "Course Created",
    //                         text:
    //                             "The course was saved successfully, but the learning resource could not be uploaded. You can add the resource from Edit Course.",
    //                     });

    //                     if (
    //                         isEditMode
    //                     ) {
    //                         if (
    //                             onSubjectUpdated
    //                         ) {
    //                             onSubjectUpdated(
    //                                 response.data
    //                             );
    //                         }
    //                     } else if (
    //                         onSubjectCreated
    //                     ) {
    //                         onSubjectCreated(
    //                             response.data
    //                         );
    //                     }

    //                     handleClose();

    //                     return;
    //                 }
    //             }

    //             showCayepAlert({
    //                 type: "success",
    //                 title:
    //                     isEditMode
    //                         ? "Course Updated"
    //                         : "Course Created",
    //                 text:
    //                     resourceFile
    //                         ? isEditMode
    //                             ? "The course and learning resource have been updated successfully."
    //                             : "The course and learning resource have been added successfully."
    //                         : isEditMode
    //                         ? "The course has been updated successfully."
    //                         : "The course has been added successfully.",
    //                 timer: 2000,
    //                 showConfirmButton:
    //                     false,
    //             });

    //             form.resetFields();

    //             setThumbnailFile(
    //                 null
    //             );

    //             setVideoFile(
    //                 null
    //             );

    //             setExistingThumbnail(
    //                 null
    //             );

    //             setExistingVideo(
    //                 null
    //             );

    //             setResourceFile(
    //                 null
    //             );

    //             setResourceForm({
    //                 title: "",
    //                 description:
    //                     "",
    //             });

    //             setResources([]);

    //             if (
    //                 isEditMode
    //             ) {
    //                 if (
    //                     onSubjectUpdated
    //                 ) {
    //                     onSubjectUpdated(
    //                         response.data
    //                     );
    //                 } else if (
    //                     onSubjectCreated
    //                 ) {
    //                     onSubjectCreated(
    //                         response.data
    //                     );
    //                 }
    //             } else if (
    //                 onSubjectCreated
    //             ) {
    //                 onSubjectCreated(
    //                     response.data
    //                 );
    //             }

    //             handleClose();
    //         } catch (error) {
    //             console.error(
    //                 isEditMode
    //                     ? "Course update error:"
    //                     : "Course creation error:",
    //                 error
    //             );

    //             console.error(
    //                 "Backend response:",
    //                 error.response?.data
    //             );

    //             const errorData =
    //                 error.response?.data;

    //             const message =
    //                 getErrorMessage(
    //                     error,
    //                     isEditMode
    //                         ? "Unable to update course."
    //                         : "Unable to create course."
    //                 );

    //             showCayepAlert({
    //                 type: "error",
    //                 title:
    //                     isEditMode
    //                         ? "Update Failed"
    //                         : "Creation Failed",
    //                 text: message,
    //             });

    //             if (
    //                 errorData?.facilitator_name
    //             ) {
    //                 form.setFields([
    //                     {
    //                         name:
    //                             "facilitator_name",
    //                         errors: [
    //                             Array.isArray(
    //                                 errorData.facilitator_name
    //                             )
    //                                 ? errorData.facilitator_name.join(
    //                                       ", "
    //                                   )
    //                                 : String(
    //                                       errorData.facilitator_name
    //                                   ),
    //                         ],
    //                     },
    //                 ]);
    //             }

    //             if (
    //                 errorData?.slots
    //             ) {
    //                 form.setFields([
    //                     {
    //                         name:
    //                             "slots",
    //                         errors: [
    //                             Array.isArray(
    //                                 errorData.slots
    //                             )
    //                                 ? errorData.slots.join(
    //                                       ", "
    //                                   )
    //                                 : String(
    //                                       errorData.slots
    //                                   ),
    //                         ],
    //                     },
    //                 ]);
    //             }
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

const handleSubmit = async (values) => {
    if (!isEditMode && !thumbnailFile) {
        showCayepAlert({
            type: "warning",
            title: "Course Thumbnail Required",
            text: "Please upload a thumbnail image for the course.",
        });

        return;
    }

    if (
        !values.facilitator_name ||
        !String(values.facilitator_name).trim()
    ) {
        showCayepAlert({
            type: "warning",
            title: "Facilitator Required",
            text: "Please enter the facilitator or instructor name for this course.",
        });

        form.setFields([
            {
                name: "facilitator_name",
                errors: [
                    "Facilitator or instructor name is required.",
                ],
            },
        ]);

        return;
    }

    const slots = Number(values.slots);

    if (!Number.isInteger(slots) || slots < 0) {
        showCayepAlert({
            type: "warning",
            title: "Invalid Course Capacity",
            text: "Course slots must be a whole number. Use 0 for unlimited enrollment.",
        });

        form.setFields([
            {
                name: "slots",
                errors: [
                    "Enter a whole number of slots. Use 0 for unlimited enrollment.",
                ],
            },
        ]);

        return;
    }

    setLoading(true);

    try {
        const formData = new FormData();

        formData.append(
            "name",
            String(values.name || "").trim()
        );

        formData.append(
            "facilitator_name",
            String(values.facilitator_name || "").trim()
        );

        formData.append(
            "division_assigned",
            String(values.division)
        );

        formData.append(
            "description",
            String(values.description || "").trim()
        );

        formData.append(
            "category",
            String(values.category || "").trim()
        );

        formData.append(
            "duration",
            String(values.duration || "").trim()
        );

        formData.append(
            "lessons",
            String(values.lessons || "").trim()
        );

        formData.append(
            "level",
            String(values.level || "").trim()
        );

        formData.append(
            "slots",
            String(slots)
        );

        if (
            values.price !== undefined &&
            values.price !== null &&
            String(values.price).trim() !== ""
        ) {
            formData.append(
                "price",
                String(values.price).trim()
            );
        }

        if (
            values.oldPrice !== undefined &&
            values.oldPrice !== null &&
            String(values.oldPrice).trim() !== ""
        ) {
            formData.append(
                "old_price",
                String(values.oldPrice).trim()
            );
        }

        if (thumbnailFile) {
            formData.append(
                "image",
                thumbnailFile,
                thumbnailFile.name
            );
        }

        /*
         * Course video is optional.
         *
         * If the user selected a video,
         * send it to the backend.
         *
         * If no video was selected,
         * do not send the video field at all.
         */
        if (videoFile) {
            formData.append(
                "video",
                videoFile,
                videoFile.name
            );
        }

        const response = isEditMode
            ? await api.patch(
                  `api/users/subjects/${subject.id}/`,
                  formData,
                  authConfig
              )
            : await api.post(
                  "api/users/subjects/",
                  formData,
                  authConfig
              );

        const courseId =
            response.data?.id || subject?.id;

        if (!courseId) {
            throw new Error(
                "The server did not return the course ID."
            );
        }

        if (resourceFile) {
            try {
                await uploadResourceToCourse(
                    courseId,
                    resourceFile,
                    resourceForm.title,
                    resourceForm.description
                );
            } catch (resourceError) {
                console.error(
                    "Course saved but resource upload failed:",
                    resourceError
                );

                showCayepAlert({
                    type: "warning",
                    title: isEditMode
                        ? "Course Updated"
                        : "Course Created",
                    text:
                        "The course was saved successfully, but the learning resource could not be uploaded. You can add the resource from Edit Course.",
                });

                if (isEditMode) {
                    if (onSubjectUpdated) {
                        onSubjectUpdated(
                            response.data
                        );
                    }
                } else if (onSubjectCreated) {
                    onSubjectCreated(
                        response.data
                    );
                }

                handleClose();

                return;
            }
        }

        showCayepAlert({
            type: "success",
            title: isEditMode
                ? "Course Updated"
                : "Course Created",
            text: resourceFile
                ? isEditMode
                    ? "The course and learning resource have been updated successfully."
                    : "The course and learning resource have been added successfully."
                : isEditMode
                ? "The course has been updated successfully."
                : "The course has been added successfully.",
            timer: 2000,
            showConfirmButton: false,
        });

        form.resetFields();

        setThumbnailFile(null);
        setVideoFile(null);
        setExistingThumbnail(null);
        setExistingVideo(null);
        setResourceFile(null);

        setResourceForm({
            title: "",
            description: "",
        });

        setResources([]);

        if (isEditMode) {
            if (onSubjectUpdated) {
                onSubjectUpdated(
                    response.data
                );
            } else if (onSubjectCreated) {
                onSubjectCreated(
                    response.data
                );
            }
        } else if (onSubjectCreated) {
            onSubjectCreated(
                response.data
            );
        }

        handleClose();
    } catch (error) {
        console.error(
            isEditMode
                ? "Course update error:"
                : "Course creation error:",
            error
        );

        console.error(
            "Backend response:",
            error.response?.data
        );

        const errorData =
            error.response?.data;

        const message = getErrorMessage(
            error,
            isEditMode
                ? "Unable to update course."
                : "Unable to create course."
        );

        showCayepAlert({
            type: "error",
            title: isEditMode
                ? "Update Failed"
                : "Creation Failed",
            text: message,
        });

        if (errorData?.facilitator_name) {
            form.setFields([
                {
                    name: "facilitator_name",
                    errors: [
                        Array.isArray(
                            errorData.facilitator_name
                        )
                            ? errorData.facilitator_name.join(
                                  ", "
                              )
                            : String(
                                  errorData.facilitator_name
                              ),
                    ],
                },
            ]);
        }

        if (errorData?.slots) {
            form.setFields([
                {
                    name: "slots",
                    errors: [
                        Array.isArray(
                            errorData.slots
                        )
                            ? errorData.slots.join(
                                  ", "
                              )
                            : String(
                                  errorData.slots
                              ),
                    ],
                },
            ]);
        }
    } finally {
        setLoading(false);
    }
};


    const handleCancel =
        () => {
            if (loading) {
                return;
            }

            form.resetFields();

            setThumbnailFile(
                null
            );

            setVideoFile(null);

            setExistingThumbnail(
                null
            );

            setExistingVideo(
                null
            );

            setResources([]);

            setResourceFile(
                null
            );

            setResourceForm({
                title: "",
                description:
                    "",
            });

            handleClose();
        };

    const currentSlots =
        Form.useWatch(
            "slots",
            form
        );

    const isUnlimited =
        Number(
            currentSlots ?? 0
        ) === 0;

    return (
        <Modal
            title={
                <div className="cayep-modal-header">
                    <div className="cayep-modal-title-icon">
                        {isEditMode ? (
                            <EditOutlined />
                        ) : (
                            <BookOutlined />
                        )}
                    </div>

                    <div>
                        <div className="cayep-modal-title">
                            {isEditMode
                                ? "Update Course"
                                : "Create New Course"}
                        </div>

                        <div className="cayep-modal-subtitle">
                            {isEditMode
                                ? "Update course information, capacity and learning materials."
                                : "Set up a professional training course for learners."}
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={
                handleCancel
            }
            footer={null}
            centered
            width={980}
            destroyOnClose
            maskClosable={
                !loading
            }
            closable={!loading}
            className="cayep-course-modal"
        >
            <div className="cayep-form-scroll">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={
                        handleSubmit
                    }
                    requiredMark="optional"
                >
                    <div className="cayep-form-section">
                        <div className="cayep-section-heading">
                            <div className="cayep-section-heading-icon">
                                <BookOutlined />
                            </div>

                            <div>
                                <div className="cayep-section-heading-title">
                                    Course Information
                                </div>

                                <div className="cayep-section-heading-text">
                                    Define the core details learners will see.
                                </div>
                            </div>
                        </div>

                        <Row
                            gutter={[
                                16,
                                0,
                            ]}
                        >
                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Course Name"
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message:
                                                "Please enter course name",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <BookOutlined />
                                        }
                                        placeholder="Ex. Leadership Mindset"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Facilitator / Instructor"
                                    name="facilitator_name"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message:
                                                "Please enter facilitator or instructor name",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <UserOutlined />
                                        }
                                        placeholder="Ex. Dr. James K. Doe"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Department"
                                    name="division"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select department",
                                        },
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        showSearch
                                        placeholder="Select Department"
                                        optionFilterProp="children"
                                        suffixIcon={
                                            <ApartmentOutlined />
                                        }
                                    >
                                        {divisions.map(
                                            (
                                                division
                                            ) => (
                                                <Option
                                                    key={
                                                        division.id
                                                    }
                                                    value={
                                                        division.id
                                                    }
                                                >
                                                    {
                                                        division.name
                                                    }
                                                </Option>
                                            )
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Category"
                                    name="category"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter course category",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <AppstoreOutlined />
                                        }
                                        placeholder="Ex. Leadership Development"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label="Level"
                                    name="level"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select course level",
                                        },
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select Level"
                                    >
                                        <Option value="Beginner">
                                            Beginner
                                        </Option>

                                        <Option value="Intermediate">
                                            Intermediate
                                        </Option>

                                        <Option value="Advanced">
                                            Advanced
                                        </Option>

                                        <Option value="All Levels">
                                            All Levels
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item
                                    label="Course Description"
                                    name="description"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message:
                                                "Please enter course description",
                                        },
                                    ]}
                                >
                                    <TextArea
                                        rows={3}
                                        placeholder="Describe what learners will gain from this course..."
                                        showCount
                                        maxLength={
                                            1000
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="cayep-form-section">
                        <div className="cayep-section-heading">
                            <div className="cayep-section-heading-icon">
                                <TeamOutlined />
                            </div>

                            <div>
                                <div className="cayep-section-heading-title">
                                    Course Structure & Capacity
                                </div>

                                <div className="cayep-section-heading-text">
                                    Configure duration, lessons, pricing and learner capacity.
                                </div>
                            </div>
                        </div>

                        <Row
                            gutter={[
                                16,
                                0,
                            ]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Duration"
                                    name="duration"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter course duration",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <ClockCircleOutlined />
                                        }
                                        placeholder="Ex. 6 Weeks"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Lessons"
                                    name="lessons"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter lesson count",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <BookOutlined />
                                        }
                                        placeholder="Ex. 24 Lessons"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Current Price"
                                    name="price"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter course price",
                                        },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <DollarOutlined />
                                        }
                                        placeholder="Ex. 49"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Old Price"
                                    name="oldPrice"
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <DollarOutlined />
                                        }
                                        placeholder="Ex. 69"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={6}
                            >
                                <Form.Item
                                    label="Course Slots"
                                    name="slots"
                                    tooltip="Use 0 when the course has no enrollment limit."
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please enter course slots",
                                        },
                                        {
                                            type: "number",
                                            min: 0,
                                            message:
                                                "Slots cannot be negative",
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        size="large"
                                        min={0}
                                        precision={0}
                                        step={1}
                                        style={{
                                            width: "100%",
                                        }}
                                        prefix={
                                            <TeamOutlined />
                                        }
                                        placeholder="Ex. 30"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={18}
                            >
                                <div
                                    className={`cayep-capacity-info ${
                                        isUnlimited
                                            ? "unlimited"
                                            : ""
                                    }`}
                                >
                                    <div className="cayep-capacity-info-icon">
                                        {isUnlimited ? (
                                            <CheckCircleOutlined />
                                        ) : (
                                            <TeamOutlined />
                                        )}
                                    </div>

                                    <div>
                                        <strong>
                                            {isUnlimited
                                                ? "Open Enrollment"
                                                : `${Number(
                                                      currentSlots ||
                                                          0
                                                  )} learner seats available`}
                                        </strong>

                                        <span>
                                            {isUnlimited
                                                ? "Set slots to 0 to allow unlimited course enrollment."
                                                : "Only the configured number of learners can enroll in this course."}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="cayep-form-section">
                        <div className="cayep-section-heading">
                            <div className="cayep-section-heading-icon">
                                <FileImageOutlined />
                            </div>

                            <div>
                                <div className="cayep-section-heading-title">
                                    Course Media
                                </div>

                                <div className="cayep-section-heading-text">
                                    Add the visual materials used to present this course.
                                </div>
                            </div>
                        </div>

                        <Row
                            gutter={[
                                16,
                                0,
                            ]}
                        >
                            <Col
                                xs={24}
                                md={12}
                            >
                                <div className="cayep-media-card">
                                    <div className="cayep-media-card-top">
                                        <div className="cayep-media-icon image">
                                            <FileImageOutlined />
                                        </div>

                                        <div>
                                            <strong>
                                                Course Thumbnail
                                            </strong>

                                            <span>
                                                JPG, PNG, WEBP · Max 5 MB
                                            </span>
                                        </div>
                                    </div>

                                    {isEditMode &&
                                        existingThumbnail &&
                                        !thumbnailFile && (
                                            <div className="cayep-existing-file compact">
                                                <FileImageOutlined />

                                                <div>
                                                    <strong>
                                                        Current thumbnail
                                                    </strong>

                                                    <span>
                                                        Existing file will remain unless replaced.
                                                    </span>
                                                </div>

                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        window.open(
                                                            existingThumbnail,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >
                                                    View
                                                </Button>
                                            </div>
                                        )}

                                    <Upload
                                        accept="image/*"
                                        maxCount={1}
                                        beforeUpload={
                                            beforeThumbnailUpload
                                        }
                                        showUploadList={
                                            false
                                        }
                                    >
                                        <Button
                                            block
                                            size="large"
                                            icon={
                                                <UploadOutlined />
                                            }
                                        >
                                            {isEditMode
                                                ? "Replace Thumbnail"
                                                : "Choose Thumbnail"}
                                        </Button>
                                    </Upload>

                                    {thumbnailFile && (
                                        <div className="cayep-selected-file compact">
                                            <FileImageOutlined />

                                            <div>
                                                <strong>
                                                    {
                                                        thumbnailFile.name
                                                    }
                                                </strong>

                                                <span>
                                                    New thumbnail selected
                                                </span>
                                            </div>

                                            <Button
                                                type="text"
                                                danger
                                                onClick={() =>
                                                    setThumbnailFile(
                                                        null
                                                    )
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <div className="cayep-media-card">
                                    <div className="cayep-media-card-top">
                                        <div className="cayep-media-icon video">
                                            <VideoCameraOutlined />
                                        </div>

                                        <div>
                                            <strong>
                                                Course Video
                                            </strong>

                                            <span>
                                                MP4, MOV, WEBM and common video formats
                                            </span>
                                        </div>
                                    </div>

                                    {isEditMode &&
                                        existingVideo &&
                                        !videoFile && (
                                            <div className="cayep-existing-file compact">
                                                <VideoCameraOutlined />

                                                <div>
                                                    <strong>
                                                        Current course video
                                                    </strong>

                                                    <span>
                                                        Existing video will remain unless replaced.
                                                    </span>
                                                </div>

                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        window.open(
                                                            existingVideo,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >
                                                    View
                                                </Button>
                                            </div>
                                        )}

                                    <Upload
                                        accept="video/*"
                                        maxCount={1}
                                        beforeUpload={
                                            beforeVideoUpload
                                        }
                                        showUploadList={
                                            false
                                        }
                                    >
                                        <Button
                                            block
                                            size="large"
                                            icon={
                                                <VideoCameraOutlined />
                                            }
                                        >
                                            {isEditMode
                                                ? "Replace Course Video"
                                                : "Choose Course Video"}
                                        </Button>
                                    </Upload>

                                    {videoFile && (
                                        <div className="cayep-selected-file compact">
                                            <VideoCameraOutlined />

                                            <div>
                                                <strong>
                                                    {
                                                        videoFile.name
                                                    }
                                                </strong>

                                                <span>
                                                    New course video selected
                                                </span>
                                            </div>

                                            <Button
                                                type="text"
                                                danger
                                                onClick={() =>
                                                    setVideoFile(
                                                        null
                                                    )
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="cayep-form-section">
                        <div className="cayep-section-heading">
                            <div className="cayep-section-heading-icon">
                                <PaperClipOutlined />
                            </div>

                            <div>
                                <div className="cayep-section-heading-title">
                                    Learning Resource
                                </div>

                                <div className="cayep-section-heading-text">
                                    Attach one supporting document to the course.
                                </div>
                            </div>
                        </div>

                        <Card
                            className="cayep-resource-card"
                            size="small"
                        >
                            <Row
                                gutter={[
                                    12,
                                    0,
                                ]}
                                align="bottom"
                            >
                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Resource Title"
                                        style={{
                                            marginBottom: 10,
                                        }}
                                    >
                                        <Input
                                            value={
                                                resourceForm.title
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setResourceForm(
                                                    (
                                                        current
                                                    ) => ({
                                                        ...current,
                                                        title:
                                                            event
                                                                .target
                                                                .value,
                                                    })
                                                )
                                            }
                                            placeholder="Ex. Leadership Training Guide"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Description"
                                        style={{
                                            marginBottom: 10,
                                        }}
                                    >
                                        <Input
                                            value={
                                                resourceForm.description
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setResourceForm(
                                                    (
                                                        current
                                                    ) => ({
                                                        ...current,
                                                        description:
                                                            event
                                                                .target
                                                                .value,
                                                    })
                                                )
                                            }
                                            placeholder="Optional description"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col
                                    xs={24}
                                    md={8}
                                >
                                    <Form.Item
                                        label="Resource File"
                                        style={{
                                            marginBottom: 10,
                                        }}
                                    >
                                        <Upload
                                            accept={
                                                RESOURCE_ACCEPT
                                            }
                                            maxCount={1}
                                            beforeUpload={
                                                beforeResourceUpload
                                            }
                                            showUploadList={
                                                false
                                            }
                                        >
                                            <Button
                                                block
                                                size="large"
                                                icon={
                                                    <UploadOutlined />
                                                }
                                            >
                                                Choose Resource
                                            </Button>
                                        </Upload>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {resourceFile ? (
                                <div className="cayep-resource-selected">
                                    <div className="cayep-resource-icon">
                                        {getResourceIcon(
                                            {
                                                name:
                                                    resourceFile.name,
                                            }
                                        )}
                                    </div>

                                    <div className="cayep-resource-details">
                                        <strong>
                                            {
                                                resourceFile.name
                                            }
                                        </strong>

                                        <span>
                                            {(
                                                resourceFile.size /
                                                1024 /
                                                1024
                                            ).toFixed(
                                                2
                                            )}{" "}
                                            MB
                                            {" • "}
                                            {getFileExtension(
                                                resourceFile
                                            ).toUpperCase()}
                                        </span>
                                    </div>

                                    <Button
                                        type="text"
                                        danger
                                        onClick={() => {
                                            setResourceFile(
                                                null
                                            );

                                            setResourceForm(
                                                {
                                                    title: "",
                                                    description:
                                                        "",
                                                }
                                            );
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <div className="cayep-resource-hint">
                                    <PaperClipOutlined />

                                    <span>
                                        No learning resource selected.
                                    </span>

                                    <Tag>
                                        PDF · Word · Excel · PPT · CSV · TXT
                                    </Tag>
                                </div>
                            )}
                        </Card>
                    </div>

                    {isEditMode && (
                        <div className="cayep-form-section existing-resources-section">
                            <div className="cayep-section-heading">
                                <div className="cayep-section-heading-icon">
                                    <PaperClipOutlined />
                                </div>

                                <div className="cayep-existing-heading-content">
                                    <div className="cayep-section-heading-title">
                                        Existing Learning Resources
                                        <Tag>
                                            {
                                                resources.length
                                            }
                                        </Tag>
                                    </div>

                                    <div className="cayep-section-heading-text">
                                        Manage resources already attached to this course.
                                    </div>
                                </div>
                            </div>

                            {resourceLoading ? (
                                <div className="cayep-loading">
                                    <Spin />

                                    <Text type="secondary">
                                        Loading resources...
                                    </Text>
                                </div>
                            ) : resources.length ===
                              0 ? (
                                <Empty
                                    image={
                                        Empty.PRESENTED_IMAGE_SIMPLE
                                    }
                                    description="No learning resources have been added yet."
                                />
                            ) : (
                                <div className="cayep-resource-list">
                                    {resources.map(
                                        (
                                            resource
                                        ) => (
                                            <div
                                                key={
                                                    resource.id
                                                }
                                                className="cayep-existing-resource"
                                            >
                                                <div className="cayep-resource-icon">
                                                    {getResourceIcon(
                                                        resource
                                                    )}
                                                </div>

                                                <div className="cayep-resource-details">
                                                    <div className="cayep-resource-title-row">
                                                        <Text
                                                            strong
                                                        >
                                                            {resource.title ||
                                                                getResourceFileName(
                                                                    resource
                                                                )}
                                                        </Text>

                                                        <Tag>
                                                            {getResourceTypeLabel(
                                                                resource
                                                            )}
                                                        </Tag>
                                                    </div>

                                                    <span>
                                                        {getResourceFileName(
                                                            resource
                                                        )}
                                                    </span>

                                                    {resource.description && (
                                                        <small>
                                                            {
                                                                resource.description
                                                            }
                                                        </small>
                                                    )}
                                                </div>

                                                <Space
                                                    size={
                                                        4
                                                    }
                                                >
                                                    <Button
                                                        type="text"
                                                        icon={
                                                            <DownloadOutlined />
                                                        }
                                                        onClick={() =>
                                                            openResource(
                                                                resource
                                                            )
                                                        }
                                                    >
                                                        <span className="cayep-action-label">
                                                            Open
                                                        </span>
                                                    </Button>

                                                    <Popconfirm
                                                        title="Delete this resource?"
                                                        description="This learning resource will be permanently removed."
                                                        okText="Delete"
                                                        cancelText="Cancel"
                                                        okButtonProps={{
                                                            danger: true,
                                                        }}
                                                        onConfirm={() =>
                                                            handleDeleteResource(
                                                                resource
                                                            )
                                                        }
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined />
                                                            }
                                                        >
                                                            <span className="cayep-action-label">
                                                                Delete
                                                            </span>
                                                        </Button>
                                                    </Popconfirm>
                                                </Space>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </Form>
            </div>

            <div className="cayep-submit-area">
                <div className="cayep-submit-summary">
                    <div className="cayep-submit-summary-icon">
                        {isEditMode ? (
                            <EditOutlined />
                        ) : (
                            <CheckCircleOutlined />
                        )}
                    </div>

                    <div>
                        <strong>
                            {isEditMode
                                ? "Ready to update course"
                                : "Ready to publish course"}
                        </strong>

                        <span>
                            {isUnlimited
                                ? "Open enrollment"
                                : `${Number(
                                      currentSlots ||
                                          0
                                  )} learner slots configured`}
                        </span>
                    </div>
                </div>

                <Space>
                    <Button
                        onClick={
                            handleCancel
                        }
                        disabled={
                            loading
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        onClick={() =>
                            form.submit()
                        }
                        loading={loading}
                        icon={
                            !loading &&
                            (isEditMode ? (
                                <EditOutlined />
                            ) : (
                                <BookOutlined />
                            ))
                        }
                    >
                        {isEditMode
                            ? "Update Course"
                            : "Create Course"}
                    </Button>
                </Space>
            </div>

            <style>{`
                .cayep-course-modal .ant-modal {
                    max-width: calc(100vw - 28px);
                }

                .cayep-course-modal .ant-modal-content {
                    padding: 0;
                    overflow: hidden;
                    border-radius: 18px;
                    background: #FFFFFF;
                    box-shadow:
                        0 24px 70px rgba(6, 42, 75, 0.22);
                }

                .cayep-course-modal .ant-modal-header {
                    margin: 0;
                    padding: 18px 24px;
                    border-bottom: 1px solid #E0E7EC;
                    background:
                        linear-gradient(
                            135deg,
                            #062A4B 0%,
                            #063A6B 100%
                        );
                }

                .cayep-course-modal .ant-modal-close {
                    top: 18px;
                    right: 18px;
                    color: rgba(255,255,255,0.85);
                    border-radius: 8px;
                }

                .cayep-course-modal .ant-modal-close:hover {
                    color: #FFFFFF;
                    background: rgba(255,255,255,0.12);
                }

                .cayep-modal-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding-right: 40px;
                }

                .cayep-modal-title-icon {
                    width: 42px;
                    height: 42px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border-radius: 11px;
                    background: rgba(246,201,68,0.16);
                    color: #F6C944;
                    font-size: 20px;
                    border: 1px solid rgba(246,201,68,0.25);
                }

                .cayep-modal-title {
                    color: #FFFFFF;
                    font-size: 19px;
                    font-weight: 800;
                    line-height: 1.2;
                }

                .cayep-modal-subtitle {
                    color: rgba(255,255,255,0.72);
                    font-size: 12px;
                    margin-top: 4px;
                    line-height: 1.4;
                }

                .cayep-form-scroll {
                    max-height: calc(100vh - 190px);
                    overflow-y: auto;
                    padding: 20px 24px 8px;
                    background: #F8FAFC;
                }

                .cayep-form-scroll::-webkit-scrollbar {
                    width: 7px;
                }

                .cayep-form-scroll::-webkit-scrollbar-track {
                    background: #EEF2F5;
                }

                .cayep-form-scroll::-webkit-scrollbar-thumb {
                    background: #C4C7C7;
                    border-radius: 10px;
                }

                .cayep-form-scroll::-webkit-scrollbar-thumb:hover {
                    background: #8E969C;
                }

                .cayep-form-section {
                    margin-bottom: 15px;
                    padding: 18px 18px 4px;
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 14px;
                    box-shadow:
                        0 3px 12px rgba(20,37,54,0.035);
                }

                .cayep-section-heading {
                    display: flex;
                    align-items: flex-start;
                    gap: 11px;
                    margin-bottom: 16px;
                    padding-bottom: 13px;
                    border-bottom: 1px solid #EDF1F4;
                }

                .cayep-section-heading-icon {
                    width: 34px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border-radius: 9px;
                    background: #F1F6FA;
                    color: #063A6B;
                    font-size: 16px;
                }

                .cayep-section-heading-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #142536;
                    font-size: 14px;
                    font-weight: 800;
                    line-height: 1.3;
                }

                .cayep-section-heading-text {
                    color: #7A8794;
                    font-size: 11.5px;
                    line-height: 1.45;
                    margin-top: 3px;
                }

                .cayep-section-heading-title .ant-tag {
                    margin: 0;
                    border: 0;
                    background: #F1F6FA;
                    color: #063A6B;
                    font-size: 11px;
                }

                .cayep-course-modal .ant-form-item {
                    margin-bottom: 14px;
                }

                .cayep-course-modal .ant-form-item-label {
                    padding-bottom: 5px;
                }

                .cayep-course-modal .ant-form-item-label > label {
                    color: #33485A;
                    font-size: 12px;
                    font-weight: 700;
                    height: auto;
                }

                .cayep-course-modal
                .ant-input,
                .cayep-course-modal
                .ant-input-number,
                .cayep-course-modal
                .ant-select-selector {
                    border-color: #D8E1E7;
                    border-radius: 9px !important;
                    box-shadow: none !important;
                }

                .cayep-course-modal
                .ant-input:hover,
                .cayep-course-modal
                .ant-input-number:hover,
                .cayep-course-modal
                .ant-select-selector:hover {
                    border-color: #8BA5B7 !important;
                }

                .cayep-course-modal
                .ant-input:focus,
                .cayep-course-modal
                .ant-input-focused,
                .cayep-course-modal
                .ant-input-number-focused,
                .cayep-course-modal
                .ant-select-focused
                .ant-select-selector {
                    border-color: #0F6DAB !important;
                    box-shadow:
                        0 0 0 2px rgba(15,109,171,0.08) !important;
                }

                .cayep-course-modal .ant-input-prefix {
                    color: #7A8794;
                    margin-right: 8px;
                }

                .cayep-course-modal .ant-input-number-prefix {
                    color: #7A8794;
                    margin-right: 7px;
                }

                .cayep-course-modal
                .ant-input-number-input {
                    height: 40px;
                }

                .cayep-course-modal
                .ant-input-number-handler-wrap {
                    border-radius: 0 8px 8px 0;
                }

                .cayep-capacity-info {
                    min-height: 62px;
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    margin-top: 1px;
                    margin-bottom: 14px;
                    padding: 10px 13px;
                    border: 1px solid #DCE8F0;
                    border-radius: 10px;
                    background: #F3F8FB;
                }

                .cayep-capacity-info.unlimited {
                    border-color: #D8EBCF;
                    background: #F5FAF1;
                }

                .cayep-capacity-info-icon {
                    width: 34px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border-radius: 9px;
                    background: #E7F1F7;
                    color: #0F6DAB;
                    font-size: 16px;
                }

                .cayep-capacity-info.unlimited
                .cayep-capacity-info-icon {
                    background: #EAF5E4;
                    color: #4FAE22;
                }

                .cayep-capacity-info strong {
                    display: block;
                    color: #142536;
                    font-size: 12px;
                    font-weight: 800;
                }

                .cayep-capacity-info span {
                    display: block;
                    color: #6B7280;
                    font-size: 11px;
                    margin-top: 2px;
                }

                .cayep-media-card {
                    height: 100%;
                    padding: 14px;
                    margin-bottom: 14px;
                    background: #F8FAFC;
                    border: 1px solid #E0E7EC;
                    border-radius: 12px;
                }

                .cayep-media-card-top {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                }

                .cayep-media-card-top > div:last-child {
                    min-width: 0;
                }

                .cayep-media-card-top strong {
                    display: block;
                    color: #142536;
                    font-size: 13px;
                    font-weight: 800;
                }

                .cayep-media-card-top span {
                    display: block;
                    color: #7A8794;
                    font-size: 10.5px;
                    margin-top: 2px;
                }

                .cayep-media-icon {
                    width: 38px;
                    height: 38px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border-radius: 10px;
                    font-size: 18px;
                }

                .cayep-media-icon.image {
                    color: #0F6DAB;
                    background: #EAF4FA;
                }

                .cayep-media-icon.video {
                    color: #C0750D;
                    background: #FFF5E7;
                }

                .cayep-existing-file {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 9px 10px;
                    margin-bottom: 9px;
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 9px;
                }

                .cayep-existing-file.compact {
                    min-height: 52px;
                }

                .cayep-existing-file > .anticon {
                    font-size: 19px;
                    color: #063A6B;
                    flex-shrink: 0;
                }

                .cayep-existing-file > div {
                    flex: 1;
                    min-width: 0;
                }

                .cayep-existing-file strong {
                    display: block;
                    color: #142536;
                    font-size: 11.5px;
                }

                .cayep-existing-file span {
                    display: block;
                    color: #6B7280;
                    font-size: 10.5px;
                    margin-top: 2px;
                }

                .cayep-selected-file {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    padding: 9px 10px;
                    margin-top: 9px;
                    background: #FFFFFF;
                    border: 1px solid #D9E7D2;
                    border-radius: 9px;
                }

                .cayep-selected-file.compact {
                    min-height: 50px;
                }

                .cayep-selected-file > .anticon {
                    color: #4FAE22;
                    font-size: 18px;
                    flex-shrink: 0;
                }

                .cayep-selected-file > div {
                    flex: 1;
                    min-width: 0;
                }

                .cayep-selected-file strong {
                    display: block;
                    color: #142536;
                    font-size: 11.5px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .cayep-selected-file span {
                    display: block;
                    color: #6B7280;
                    font-size: 10.5px;
                    margin-top: 2px;
                }

                .cayep-resource-card {
                    background: #F7F9FB !important;
                    border-color: #E0E7EC !important;
                    border-radius: 11px !important;
                    margin-bottom: 14px;
                }

                .cayep-resource-card .ant-card-body {
                    padding: 13px !important;
                }

                .cayep-resource-selected {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 9px 10px;
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 9px;
                }

                .cayep-resource-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #EEF4F8;
                    color: #063A6B;
                    font-size: 19px;
                    flex-shrink: 0;
                }

                .cayep-resource-details {
                    flex: 1;
                    min-width: 0;
                }

                .cayep-resource-details strong {
                    display: block;
                    color: #142536;
                    font-weight: 700;
                    font-size: 12px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .cayep-resource-details > span {
                    display: block;
                    color: #6B7280;
                    font-size: 10.5px;
                    margin-top: 2px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .cayep-resource-hint {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 9px 10px;
                    color: #6B7280;
                    background: #FFFFFF;
                    border: 1px dashed #C4C7C7;
                    border-radius: 9px;
                    font-size: 11px;
                }

                .cayep-resource-hint .ant-tag {
                    margin-left: auto;
                    margin-right: 0;
                    font-size: 9px;
                    border: 0;
                    background: #F1F4F6;
                    color: #687684;
                }

                .existing-resources-section {
                    padding-bottom: 15px;
                }

                .cayep-existing-heading-content {
                    min-width: 0;
                }

                .cayep-resource-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .cayep-existing-resource {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 9px 10px;
                    background: #FFFFFF;
                    border: 1px solid #E0E7EC;
                    border-radius: 9px;
                }

                .cayep-existing-resource
                .cayep-resource-icon {
                    width: 36px;
                    height: 36px;
                }

                .cayep-resource-title-row {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    flex-wrap: wrap;
                }

                .cayep-existing-resource
                .cayep-resource-details
                > span {
                    display: block;
                    font-size: 10.5px;
                    color: #6B7280;
                    margin-top: 2px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .cayep-existing-resource
                .cayep-resource-details
                small {
                    display: block;
                    color: #6B7280;
                    margin-top: 2px;
                    font-size: 10.5px;
                }

                .cayep-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 22px 0;
                }

                .cayep-submit-area {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    padding: 13px 24px;
                    border-top: 1px solid #E0E7EC;
                    background: #FFFFFF;
                }

                .cayep-submit-summary {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    min-width: 0;
                }

                .cayep-submit-summary-icon {
                    width: 34px;
                    height: 34px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border-radius: 9px;
                    background: #F1F6FA;
                    color: #063A6B;
                }

                .cayep-submit-summary strong {
                    display: block;
                    color: #142536;
                    font-size: 11.5px;
                    font-weight: 800;
                }

                .cayep-submit-summary span {
                    display: block;
                    color: #7A8794;
                    font-size: 10.5px;
                    margin-top: 1px;
                }

                .cayep-submit-area .ant-btn {
                    min-width: 105px;
                    border-radius: 8px;
                    font-weight: 700;
                }

                .cayep-submit-area
                .ant-btn-primary {
                    background: #062A4B;
                    border-color: #062A4B;
                }

                .cayep-submit-area
                .ant-btn-primary:hover {
                    background: #063A6B !important;
                    border-color: #063A6B !important;
                }

                .cayep-swal-popup {
                    width: min(430px, calc(100vw - 28px)) !important;
                    border-radius: 18px !important;
                    padding: 24px !important;
                    font-family: inherit !important;
                }

                .cayep-swal-title {
                    color: #062A4B !important;
                    font-size: 20px !important;
                    font-weight: 800 !important;
                    margin-top: 4px !important;
                }

                .cayep-swal-text {
                    color: #526273 !important;
                    font-size: 14px !important;
                    line-height: 1.65 !important;
                    white-space: pre-line !important;
                }

                .cayep-swal-confirm {
                    min-width: 110px !important;
                    min-height: 40px !important;
                    border-radius: 9px !important;
                    font-weight: 700 !important;
                    border: 0 !important;
                }

                .swal2-icon {
                    margin-top: 2px !important;
                }

                .swal2-timer-progress-bar {
                    background: #F6C944 !important;
                }

                .swal2-popup {
                    animation: cayepAlertIn 0.22s ease-out !important;
                }

                @keyframes cayepAlertIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px) scale(0.97);
                    }

                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                @media (max-width: 767px) {
                    .cayep-form-scroll {
                        max-height: calc(100vh - 205px);
                        padding: 14px 12px 5px;
                    }

                    .cayep-form-section {
                        padding: 15px 13px 3px;
                        border-radius: 12px;
                    }

                    .cayep-section-heading {
                        margin-bottom: 13px;
                    }

                    .cayep-submit-area {
                        padding: 12px;
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .cayep-submit-summary {
                        width: 100%;
                    }

                    .cayep-submit-area > .ant-space {
                        width: 100%;
                    }

                    .cayep-submit-area > .ant-space .ant-space-item {
                        flex: 1;
                    }

                    .cayep-submit-area .ant-btn {
                        width: 100%;
                    }

                    .cayep-resource-hint {
                        align-items: flex-start;
                        flex-wrap: wrap;
                    }

                    .cayep-resource-hint .ant-tag {
                        width: 100%;
                        margin-left: 0;
                    }

                    .cayep-existing-resource {
                        align-items: flex-start;
                        flex-wrap: wrap;
                    }

                    .cayep-existing-resource
                    .cayep-resource-details {
                        min-width: calc(100% - 50px);
                    }

                    .cayep-existing-resource
                    > .ant-space {
                        width: 100%;
                        justify-content: flex-end;
                    }

                    .cayep-action-label {
                        display: none;
                    }
                }

                @media (max-width: 576px) {
                    .cayep-course-modal .ant-modal {
                        max-width: calc(100vw - 12px);
                        margin: 6px auto;
                    }

                    .cayep-course-modal .ant-modal-header {
                        padding: 15px 14px;
                    }

                    .cayep-modal-title {
                        font-size: 16px;
                    }

                    .cayep-modal-subtitle {
                        font-size: 10.5px;
                    }

                    .cayep-modal-title-icon {
                        width: 36px;
                        height: 36px;
                        font-size: 17px;
                    }

                    .cayep-existing-file {
                        align-items: flex-start;
                    }

                    .cayep-selected-file {
                        align-items: flex-start;
                    }

                    .cayep-selected-file > .ant-btn {
                        margin-left: auto;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default SubjectFormModal;